// NotificationsBox.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { compareDateTime } from '../../utilities/utility';
import { SocketContext } from '../../context/socketContext';
import { ApiContext } from '../../context/apiContext';
import {
    NotificationsBox,
    NotificationItem,
    TextDiv,
    TimeDiv,
    NoNotificationsText,
    NoNotificationsDiv,
    LoadingDiv,
} from './notifications-box.styles'; // Adjust the import path as needed

const NotificationsBoxComponent = () => {
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [nextPageUrl, setNextPageUrl] = useState(null); // Store the next page URL
    const navigate = useNavigate();
    const api = useContext(ApiContext);
    const { subscribeToNewNotifications } = useContext(SocketContext);

    const fetchNotifications = async (url = null) => {
        try {
            const isLocalhost = window.location.hostname === 'localhost';
            let requestUrl = url ? url : '/api/notification/list';
            if (!isLocalhost && requestUrl.startsWith('http://')) {
                requestUrl = requestUrl.replace('http://', 'https://');
            }
            const data = await api.get(requestUrl);
            setNotifications((prev) => (url ? [...prev, ...data.results] : data.results));
            setNextPageUrl(data.next);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // lock body scroll
        const originalStyle = window.getComputedStyle(document.body).overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = originalStyle;
        };
    }, []);

     // Adding a manual scroll event listener
     useEffect(() => {
        const handleScroll = (e) => {
            const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
            // Check if scrolled to the bottom
            if (scrollHeight - scrollTop <= clientHeight * 1.05 && nextPageUrl) {
                // 105% threshold to ensure it's at the bottom
                fetchNotifications(nextPageUrl);
            }
        };

        const box = document.querySelector('#notificationsBox'); // Ensure your NotificationsBox has this ID
        if (box) {
            box.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (box) {
                box.removeEventListener('scroll', handleScroll);
            }
        };
    }, [nextPageUrl]);

    useEffect(() => {
        fetchNotifications();
    }, []);

    useEffect(() => {
        const unsubscribe = subscribeToNewNotifications((newNotifications) => {
            setNotifications((prev) => [newNotifications, ...prev]);
        });

        return () => {
            unsubscribe();
        };
    }, [subscribeToNewNotifications]);

    const handleNotificationClick = (url) => {
        if (!url) return;
        if (/^https?:\/\//i.test(url)) {
            window.location.href = url;
            return;
        }
        navigate(url);
    };

    return (
        <NotificationsBox id="notificationsBox">
            {!isLoading ? (
                <>
                    {notifications.length > 0 ? (
                        notifications.map((notification, index) => (
                            <NotificationItem
                                key={index}
                                unread={!notification.read}
                                onClick={() => handleNotificationClick(notification.url)}
                            >
                                <TextDiv>{notification.content}</TextDiv>
                                <TimeDiv>{compareDateTime(notification.createdAt)}</TimeDiv>
                            </NotificationItem>
                        ))
                    ) : (
                        <NoNotificationsDiv>
                            <NoNotificationsText>No new notifications</NoNotificationsText>
                        </NoNotificationsDiv>
                    )}
                    {notifications.length > 0 && nextPageUrl && <LoadingDiv>Loading...</LoadingDiv>}
                </>
            ) : (
                <NoNotificationsDiv>
                    <NoNotificationsText>Loading Notifications...</NoNotificationsText>
                </NoNotificationsDiv>
            )}
        </NotificationsBox>
    );
};

export default NotificationsBoxComponent;
